var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import MUIDataTable from 'mui-datatables';
import { Typography } from '../typography';
import { Pagination } from './pagination';
import { PaginationCounter } from './pagination-counter';
import { TableWrapper } from './table-wrapper';
import { prepareColumnsConfig } from './utils';
import { getDefaultOptions } from './constants';
import styles from './table.module.css';
import { TableCell, TableRow, useMediaQuery, useTheme } from '@mui/material';
export const Table = (props) => {
    var _a, _b, _c, _d;
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { isRowsSortable, selectableRows, selectedRowIds, onRowsSorted, onRowsSelected, data, columns: columnsConfig, options, title, loading, rowVariant, forceShowingPagination } = props, rest = __rest(props, ["isRowsSortable", "selectableRows", "selectedRowIds", "onRowsSorted", "onRowsSelected", "data", "columns", "options", "title", "loading", "rowVariant", "forceShowingPagination"]);
    const totalCount = (options === null || options === void 0 ? void 0 : options.count) || (data === null || data === void 0 ? void 0 : data.length);
    const defaultOptions = getDefaultOptions({
        selectableRows,
        visibleItemsCount: data === null || data === void 0 ? void 0 : data.length,
        totalCount,
        isMobile: isMobile || isTablet,
    });
    const mergedOptions = Object.assign(Object.assign(Object.assign({}, defaultOptions), options), { count: defaultOptions.count });
    const { pagination: isPaginationEnabled, rowsPerPage } = mergedOptions;
    const ref = useRef(null);
    const [tableBodyContainer, setTableBodyContainer] = useState();
    const [page, setPage] = useState(0);
    const [count, setCount] = useState(totalCount);
    const showPagination = (isPaginationEnabled && count > rowsPerPage) || forceShowingPagination;
    const hideNoMatchLabel = !loading && !count && ((_b = (_a = options.textLabels) === null || _a === void 0 ? void 0 : _a.body) === null || _b === void 0 ? void 0 : _b.noMatch) === null;
    const isTableWithOffset = showPagination || !!title;
    const columns = useMemo(() => prepareColumnsConfig(columnsConfig, {
        isRowsSortable,
        isMobile: isMobile || isTablet,
    }), [columnsConfig, isRowsSortable, isMobile, isTablet]);
    // @ts-ignore
    const withActions = ((_c = columns[columns.length - 1]) === null || _c === void 0 ? void 0 : _c.name) === 'actions';
    useEffect(() => {
        if (typeof mergedOptions.page !== 'undefined') {
            setPage(mergedOptions.page);
        }
    }, [mergedOptions.page]);
    // do not reset total count number through loading
    useEffect(() => {
        if (!loading) {
            setCount(totalCount);
        }
    }, [totalCount, loading]);
    useEffect(() => {
        var _a;
        if (!isRowsSortable) {
            return;
        }
        const tableBody = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.querySelector('tbody');
        setTableBodyContainer(tableBody);
    }, [isRowsSortable, data]);
    const onPageChange = (page) => {
        var _a;
        setPage(page);
        (_a = mergedOptions.onChangePage) === null || _a === void 0 ? void 0 : _a.call(mergedOptions, page);
    };
    const getPagination = () => (_jsx("div", { className: styles.pagination, children: _jsx(Pagination, { page: page, count: totalCount, rowsPerPage: mergedOptions.rowsPerPage, onChange: onPageChange }) }));
    const selectedRowIndexes = useMemo(() => {
        return ((selectedRowIds === null || selectedRowIds === void 0 ? void 0 : selectedRowIds.map((id) => {
            const index = data.findIndex((row) => row.id === id);
            return index;
        })) || []);
    }, [data, selectedRowIds]);
    const onRowSelectionChange = (currentRowsSelected, allRowsSelected, selectedRowsIndexes) => {
        if (!selectedRowsIndexes) {
            return;
        }
        const newSelectedRowIdsSet = new Set(selectedRowIds);
        data === null || data === void 0 ? void 0 : data.forEach((item, index) => {
            var _a, _b;
            if (!item.id) {
                return;
            }
            if (selectedRowsIndexes.includes(index)) {
                newSelectedRowIdsSet.add((_a = item.id) === null || _a === void 0 ? void 0 : _a.toString());
            }
            else {
                newSelectedRowIdsSet.delete((_b = item.id) === null || _b === void 0 ? void 0 : _b.toString());
            }
        });
        onRowsSelected === null || onRowsSelected === void 0 ? void 0 : onRowsSelected(Array.from(newSelectedRowIdsSet), currentRowsSelected, allRowsSelected, selectedRowsIndexes);
    };
    const isRowExpandable = (dataIndex) => { var _a; return Boolean((_a = data[dataIndex]) === null || _a === void 0 ? void 0 : _a.expandableContent); };
    const renderExpandableRow = (rowData, { dataIndex }) => {
        if (!isRowExpandable(dataIndex)) {
            return null;
        }
        const colSpan = rowData.length + 1;
        const row = data[dataIndex];
        return (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: colSpan, children: row.expandableContent }) }));
    };
    return (_jsxs("div", { className: clsx({
            [styles.tableWrapperWithActions]: withActions,
            [styles.tableWrapperWithDraggableSorting]: isRowsSortable,
            [styles.tableWrapperWithSelectableRows]: selectableRows,
            [styles.noMatchLabelHidden]: hideNoMatchLabel,
        }), "data-testid": "table", ref: ref, children: [_jsxs("div", { className: styles.titleWrapper, children: [title && (_jsx(Typography, { variant: "subtitle1", fontWeight: "bold", className: styles.title, "data-testid": "table-title", children: title })), !title && showPagination && (_jsx(PaginationCounter, { count: count, rowsPerPage: rowsPerPage, page: page })), showPagination && !!(data === null || data === void 0 ? void 0 : data.length) && getPagination()] }), _jsx("div", { className: clsx({
                    [styles.tableWrapperWithOffset]: isTableWithOffset,
                    [styles.tableRowPointer]: mergedOptions.onRowClick,
                    [styles.tableRowBorder]: rowVariant === 'outlined',
                }), children: _jsx(TableWrapper, { tableBodyContainer: tableBodyContainer, data: data, isRowsSortable: isRowsSortable, onRowsSorted: onRowsSorted, children: _jsx(MUIDataTable, Object.assign({ title: undefined, data: data, columns: columns, options: Object.assign(Object.assign({}, mergedOptions), { page: undefined, pagination: false, textLabels: Object.assign(Object.assign({}, mergedOptions.textLabels), { body: Object.assign({ noMatch: loading
                                        ? t('table.loading', 'Loading...')
                                        : t('table.no-data', 'Sorry, no matching records found') }, (_d = mergedOptions === null || mergedOptions === void 0 ? void 0 : mergedOptions.textLabels) === null || _d === void 0 ? void 0 : _d.body) }), rowsSelected: selectedRowIndexes, onRowSelectionChange,
                            isRowExpandable,
                            renderExpandableRow, expandableRowsHeader: false, rowsExpanded: [...Array(data.length).keys()] }), components: {
                            ExpandButton: () => null,
                        } }, rest)) }) }), showPagination && !!(data === null || data === void 0 ? void 0 : data.length) && getPagination()] }));
};
export default Table;
